import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrderCatalogItemDTO } from '@bifeai/shared';
import { Dialog, EmptyState, Field, Input, Select, Skeleton, Switch, useToast } from '@bifeai/ui';
import { PackageX } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';

/**
 * "Hay / no hay": lo que se terminó por hoy en una sucursal. bifi no lo ofrece ni lo deja pedir hasta que se
 * vuelva a marcar que hay. Sirve sobre todo cuando BifeAI no lleva el stock (Solo bifi).
 */
export function SoldOutDialog({ onClose }: { onClose: () => void }) {
  const { me, selectedBranchId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const own = me.branches.filter((b) => !b.franchise || me.branches.length === 1);
  const [branchId, setBranchId] = useState<string>(selectedBranchId ?? own[0]?.id ?? '');
  const [search, setSearch] = useState('');
  const key = ['orders-chat', 'catalog', branchId, 'sold-out'];
  const catalog = useQuery({
    queryKey: key,
    queryFn: () => api.get<OrderCatalogItemDTO[]>(`/orders-chat/catalog?branchId=${branchId}`),
    enabled: !!branchId,
  });
  const toggle = useMutation({
    mutationFn: (v: { productId: string; soldOut: boolean }) => api.put<{ soldOut: boolean }>(`/orders-chat/sold-out/${branchId}`, v),
    onMutate: (v) => {
      qc.setQueryData<OrderCatalogItemDTO[]>(key, (list) => list?.map((p) => (p.id === v.productId ? { ...p, soldOut: v.soldOut } : p)));
    },
    onError: (err) => {
      toast.error('No se pudo guardar', errorMessage(err));
      void qc.invalidateQueries({ queryKey: key });
    },
  });
  const q = search.trim().toLowerCase();
  // Lo que no hay va arriba, para volver a marcarlo rápido cuando llega.
  const list = (catalog.data ?? [])
    .filter((p) => !q || p.name.toLowerCase().includes(q) || (p.category ?? '').toLowerCase().includes(q))
    .sort((a, b) => Number(b.soldOut) - Number(a.soldOut));
  const out = (catalog.data ?? []).filter((p) => p.soldOut).length;

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Hay / no hay"
      description="Lo que se terminó por hoy: bifi no lo ofrece ni lo deja pedir hasta que vuelvas a marcar que hay."
    >
      <div className="bf-form">
        {own.length > 1 && (
          <Field label="Sucursal">
            <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {own.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={branchId ? 'Buscar producto' : 'Primero elegí la sucursal'}
          disabled={!branchId}
          aria-label="Buscar producto"
        />
        {branchId && catalog.isPending && <Skeleton style={{ height: 200 }} />}
        {branchId && catalog.data && (
          <>
            <p className="bf-cell-sub">{out === 0 ? 'Hay de todo.' : `${out} ${out === 1 ? 'producto marcado' : 'productos marcados'} como que no hay.`}</p>
            {list.length === 0 ? (
              <EmptyState icon={<PackageX />} title="Sin productos" text="No hay productos con precio que coincidan con la búsqueda." />
            ) : (
              <ul className="bf-soldout-list">
                {list.map((p) => (
                  <li key={p.id} className={p.soldOut ? 'is-out' : undefined}>
                    <div>
                      <strong>{p.name}</strong>
                      <span className="bf-cell-sub">
                        {formatMoney(p.priceCents)}
                        {p.unitType === 'WEIGHT' ? ' el kilo' : ' la unidad'}
                        {p.category ? ` · ${p.category}` : ''}
                      </span>
                    </div>
                    <span className="bf-soldout-state">{p.soldOut ? 'No hay' : 'Hay'}</span>
                    <Switch
                      checked={!p.soldOut}
                      onCheckedChange={(hay) => toggle.mutate({ productId: p.id, soldOut: !hay })}
                      label={`${p.name}: ${p.soldOut ? 'no hay' : 'hay'}`}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
